import React, { useEffect, useMemo, useState } from "react"
import { Search } from "lucide-react"
import WebsiteDetails from "../components/WebsiteDetails"
import { normalizeGames } from "../utils/games"

const gameFilters = [
  { value: "all", label: "All" },
  { value: "cs", label: "CS" },
  { value: "rust", label: "RUST" },
]

const AllWebsites = () => {
  const [websites, setWebsites] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [sortType, setSortType] = useState("rtp")
  const [gameFilter, setGameFilter] = useState("all")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError("")

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/websites`)
        if (!response.ok) throw new Error(`Request failed with status ${response.status}`)

        const data = await response.json()
        setWebsites(Array.isArray(data) ? data : [])
      } catch (requestError) {
        console.error("Failed to fetch websites:", requestError)
        setWebsites([])
        setError("We couldn't load the websites. Please try again later.")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const filteredWebsites = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()

    return [...websites]
      .filter((website) => {
        const matchesSearch = String(website.websiteName || "").toLowerCase().includes(query)
        const games = normalizeGames(website.games)
        const matchesGame = gameFilter === "all" ||
          games.includes(gameFilter) ||
          (gameFilter === "cs" && games.includes("csgo"))

        return matchesSearch && matchesGame
      })
      .sort((left, right) => {
        if (sortType === "rtp") {
          return (Number(right.websiteRTP) || 0) - (Number(left.websiteRTP) || 0)
        }
        if (sortType === "name") {
          return String(left.websiteName || "").localeCompare(String(right.websiteName || ""))
        }
        return 0
      })
  }, [websites, searchQuery, sortType, gameFilter])

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="relative mb-12 text-center">
        <h1 className="mb-4 bg-gradient-to-r from-[#FF6B00] via-[#FF9A3D] to-[#FFD700] bg-clip-text font-display text-4xl font-bold text-transparent drop-shadow-glow sm:text-5xl">
          Case Opening Websites
        </h1>
        <p className="mx-auto max-w-2xl text-blue-100/70">
          Compare case opening websites, supported games, and average RTP in one place.
        </p>
      </header>

      <section>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="font-display text-3xl font-semibold text-white">Explore Websites</h2>
            <p className="mt-1 text-sm text-blue-100/60">
              Find the website that best matches how you want to play.
            </p>
          </div>

          {!loading && !error && (
            <span className="w-fit rounded-full border border-[#2A2D31]/60 bg-[#1A1D21]/50 px-3 py-1 text-sm text-blue-100/70">
              {filteredWebsites.length} {filteredWebsites.length === 1 ? "result" : "results"}
            </span>
          )}
        </div>

        <div className="mb-8 flex flex-col gap-4 rounded-2xl border border-[#2A2D31]/50 bg-[#1A1D21]/40 p-4 shadow-[0_8px_30px_rgba(0,0,0,0.12)] backdrop-blur-sm lg:flex-row">
          <label className="relative flex-1">
            <span className="sr-only">Search websites</span>
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-100/40" />
            <input
              type="search"
              placeholder="Search websites..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="w-full rounded-xl border border-[#2A2D31]/60 bg-[#0F1215]/80 py-3 pl-10 pr-4 text-sm text-white outline-none transition-colors placeholder:text-blue-100/40 focus:border-[#FF6B00]/60 focus:ring-2 focus:ring-[#FF6B00]/10"
            />
          </label>

          <label className="relative lg:w-52">
            <span className="sr-only">Sort websites</span>
            <select
              value={sortType}
              onChange={(event) => setSortType(event.target.value)}
              className="w-full appearance-none rounded-xl border border-[#2A2D31]/60 bg-[#0F1215]/80 px-4 py-3 pr-10 text-sm text-white outline-none transition-colors focus:border-[#FF6B00]/60 focus:ring-2 focus:ring-[#FF6B00]/10"
            >
              <option value="rtp">Highest RTP</option>
              <option value="name">Name (A-Z)</option>
            </select>
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
              className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-100/50"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </label>

          <div className="grid grid-cols-3 gap-2 rounded-xl border border-[#2A2D31]/40 bg-[#0F1215]/45 p-1">
            {gameFilters.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => setGameFilter(filter.value)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                  gameFilter === filter.value
                    ? "!bg-gradient-to-r from-[#FF6B00] to-[#FF9A3D] text-white shadow-[0_6px_18px_rgba(255,107,0,0.18)]"
                    : "!bg-transparent text-blue-100/70 hover:text-white"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {error ? (
          <div className="rounded-2xl border border-red-400/20 bg-red-400/5 px-6 py-12 text-center text-red-100/80">
            {error}
          </div>
        ) : loading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[0, 1, 2, 3].map((item) => (
              <div
                key={item}
                className="h-96 animate-pulse rounded-2xl border border-[#2A2D31]/40 bg-[#1A1D21]/40"
              />
            ))}
          </div>
        ) : filteredWebsites.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredWebsites.map((website, index) => (
              <WebsiteDetails
                key={website._id || `${website.websiteName}-${index}`}
                websiteName={website.websiteName}
                websiteRTP={website.websiteRTP}
                websiteLogo={website.websiteLogo}
                websiteGames={website.games}
                websiteDescription={website.websiteDescription}
                totalCases={website.totalCases}
                subpageLink={website.subpageLink}
                affiliateLink={website.affiliateLink}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-[#2A2D31]/50 bg-[#0F1215]/60 px-6 py-14 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#FF6B00]/10 text-[#FF9A3D]">
              <Search className="h-5 w-5" />
            </div>
            <h3 className="font-display text-lg font-semibold text-white">No websites found</h3>
            <p className="mt-1 text-sm text-blue-100/60">
              Try a different search or game filter.
            </p>
          </div>
        )}
      </section>
    </div>
  )
}

export default AllWebsites
